import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { CatalogsService } from '../catalogs.service'
import { CatalogsRepository } from '../catalogs.repository'
import { CatalogSearchParams } from '../models/catalog-search.model'
import { Catalog } from '../models/catalog.entity'
import { Page } from '../../../models/util.model'

interface CatalogsListingState {
  filters?: CatalogSearchParams
  data?: Catalog[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const CATALOGS_LISTING_INITIAL_STATE: CatalogsListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class CatalogsListingStore {
  constructor(
    private catalogsService: CatalogsService,
    private catalogsRepository: CatalogsRepository,
  ) {}

  private state = signal<CatalogsListingState>(CATALOGS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly catalogs = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)

  // Effects

  async search(
    params?: CatalogSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.catalogsRepository
        .searchCatalogs$(
          this.parseStateFilterParams(this.state(), params),
          returnAll,
        )
        .subscribe({
          next: (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async delete(catalog: Catalog): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.catalogsRepository
        .alert('CATALOG_DELETION', catalog)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.catalogsService.delete$(catalog._id).subscribe({
              next: () => {
                resolve(true)
                this.catalogsRepository.notify({
                  code: 'CATALOG_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(data: Page<Catalog>) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: CatalogSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: CatalogSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: CatalogSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      const updatedFilters = { ...currentFilters }

      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof CatalogSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: CatalogsListingState,
    filters?: CatalogSearchParams,
  ): CatalogSearchParams {
    const params: CatalogSearchParams = filters || {}

    return params
  }
}
